import { useState, useEffect, useReducer, Fragment } from "react";
import { axiosPut, axiosDelete, axiosGet } from "@/services/axiosService";
import { useDispatch } from "react-redux";
import { userSlice } from "@/redux/User";
import NoResultFound from "@/components/shared/noResultFound/noResultFound";
import Skeleton from "react-loading-skeleton";
import * as MetaService from "@/services/facebook.service";
import "../../../src/styles/config.css";
import { AnimatePresence, motion } from "framer-motion";
import { useSearchParams, useNavigate } from "react-router-dom";
import useInstagramConfig from "@/pages/omni-builder/react-query/configs/useInstagramConfig";
import { useQueryClient } from "@tanstack/react-query";
import { DeleteBot } from "./Telegram";
import {
  ConnectIcon,
  DisconnectIcon,
} from "@/pages/cbb-builder/components/Channels/components/WhatsappChannel";
import BooleanModal from "../builder/BooleanModal";
import ModalWrapper from "@/pages/cbb-builder/components/ModalWrapper";

export default function Instagram({ onClose, botCustomId, liveBotId }) {
  const dispatch = useDispatch();
  const route = useNavigate();
  const queryClient = useQueryClient();
  const [state, setState] = useState(0);
  const [accounts, setaccounts] = useState<any[]>([]);
  const [search, setSearch] = useState<string | undefined>();
  const [accountsLoader, setAccountsLoader] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(null);
  const [searchParams] = useSearchParams();
  const [isInstagramDelete, setIsInstagramDelete] = useState<boolean>(false);
  const instagramConfiguration = useInstagramConfig();
  const { isError, isFetching, isLoading, data } = instagramConfiguration ?? {};
  const [connection, setConnection] = useState<any>(false);

  const [connectionStatus, setConnectionStatus] = useState({});
  const handleDropdown = (index) => {
    setIsOpen(isOpen === index ? null : index);
  };

  useEffect(() => {
    if (isFetching || isLoading) {
      setAccountsLoader(true);
      return;
    }

    if (isError) {
      console.error("Error:", instagramConfiguration.error || "Unknown error");
      dispatch(
        userSlice.actions.setToastPopUp({
          show: true,
          toastType: "error",
          message: "Error while fetching Instagram configuration",
        }),
      );
      onClose();
      return;
    }

    if (data && data.length > 0) {
      setaccounts(data);
      applyStatusOfAccounts(data);
      setState(1);
      setAccountsLoader(false);
    } else {
      // No accounts found, try to add new config
      const forcedLogin = searchParams.get("forced");
      const forced = forcedLogin === "true" ? true : false;
      const loginDone = searchParams.get("loginDone");

      if (forced) {
        addNewConfig(forced);
      } else if (loginDone === "true") {
        route(window.location.pathname + "?loginDone=false");
      } else {
        addNewConfig();
      }
      setAccountsLoader(false);
    }
  }, [isError, isFetching, isLoading, data, searchParams]);

  const deleteConfig = async (id?: string) => {
    try {
      setAccountsLoader(true);
      let res = await axiosPut(
        `/configurations/deleteAppConfig?id=${id}&siteName=instagram`,
      );
      res = await res.data;
      await _sendSuccessToast(
        "Your Instagram account disconnected successfully",
      );

      // Invalidate and refetch the query
      queryClient.invalidateQueries({
        queryKey: ["configurations", "instagram"],
      });

      onClose();
    } catch (err) {
      console.log("error while disconnecting Account");
    } finally {
      setAccountsLoader(false);
    }
  };

  //connect & disconnect account with the bot
  const handleAcc = async (type: string, acc: any) => {
    try {
      if (!liveBotId || liveBotId === undefined || liveBotId === "undefined")
        return await _sendErrorToast("Bot is not live yet!");

      // setAccountsLoader(true);
      setConnection(true);
      setConnectionStatus((prev) => ({ ...prev, [acc._id]: true }));
      if (type === "connect" && !acc?.showPrompt) {
        let tempArr = accounts.map((account) => {
          if (account?.botId && account?._id === acc?._id) {
            return { ...account, showPrompt: true };
          } else return { ...account, showPrompt: false };
        });
        setaccounts(tempArr);
        if (tempArr.some((data) => data.showPrompt)) return;
      }

      let res = await axiosPut(`/configurations/${type}Acccount`, {
        siteName: "instagram",
        _id: acc?._id,
        botId: liveBotId,
        botCustomId,
      });
      if (!res.data.success) {
        dispatch(
          userSlice.actions.setToastPopUp({
            show: true,
            toastType: "error",
            message: res.data?.message ?? "something went wrong",
          }),
        );
      }
      // Invalidate and refetch the query instead of manual fetch
      queryClient.invalidateQueries({
        queryKey: ["configurations", "instagram"],
      });

      await _sendSuccessToast(`Your Instagram account ${type}ed successfully`);
      onClose();
    } catch (err) {
      await _sendErrorToast(err);
    } finally {
      setAccountsLoader(false);
      setConnection(false);
      setConnectionStatus((prev) => ({ ...prev, [acc._id]: false }));
    }
  };

  async function applyStatusOfAccounts(accounts: any) {
    try {
      setAccountsLoader(true);
      let tempArr = accounts
        .map((account) => {
          if (liveBotId && account.botId === liveBotId)
            return { ...account, showPrompt: false, connected: true };
          else return { ...account, showPrompt: false, connected: false };
        })
        .sort((a, b) => {
          return b.connected - a.connected;
        });
      setaccounts(tempArr);
    } catch (err: any) {
      await _sendErrorToast(err);
    } finally {
      setAccountsLoader(false);
    }
  }

  async function addNewConfig(forced: boolean = false) {
    const loginLink = await MetaService.instagramLogin(forced);
    const authWindow = window.open(loginLink, "_blank", "width=600,height=600");
    const checkClosed = setInterval(
      async () => {
        if (authWindow?.closed) {
          const response = await axiosGet(
            "/configurations/getAppConfig?siteName=instagram",
          );
          if (response.data && response.data.length > 0) {
            // Invalidate and refetch the query instead of manual fetch
            queryClient.invalidateQueries({
              queryKey: ["configurations", "instagram"],
            });
          } else onClose();
          clearInterval(checkClosed);
        }
      },
      forced ? 5000 : 1000,
    );
  }

  const handlePrompt = (show: boolean, _id: string) => {
    let tempArr = accounts?.map((account) => {
      if (_id === account._id) return { ...account, showPrompt: show };
      else return account;
    });
    setaccounts(tempArr);
  };

  async function _sendErrorToast(err?: any) {
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "error",
        message: !!err
          ? typeof err === "string"
            ? err
            : typeof err?.response?.data?.message === "string"
              ? err?.response?.data?.message
              : "Something went wrong"
          : "Something went wrong",
      }),
    );
  }

  async function _sendSuccessToast(msg?: any) {
    dispatch(
      userSlice.actions.setToastPopUp({
        show: true,
        toastType: "success",
        message: msg,
      }),
    );
  }

  if (!state) return null;

  return (
    <>
      {/* {!instagramConfiguration?.data && ( */}
      <div className="mt-[10px]">
        <h4 className="mb-[10px] text-[16px] font-medium text-text-clr max-[1280px]:!text-[14px]">
          Connected Account
        </h4>

        <div className="fb-pages skeletonPages rounded-[20px] bg-[#fafafa] p-[20px]">
          <h3 className="text-[14px] !font-normal leading-[110%] !text-[#666] max-[1280px]:!text-[13px]">
            Details
          </h3>
          {accountsLoader ? (
            <div className="fb-list whatsapp_table overflow-x-auto">
              <table className="mt-[10px] min-w-full overflow-hidden rounded-[10px] border border-[rgba(167,80,80,0.1)]">
                <thead className="flex w-full rounded-t-[10px] border border-[rgba(0,0,0,0.10)] bg-gray-100 text-gray-700">
                  <tr className="grid">
                    <th className="px-[20px] py-[13px]">Bot Name</th>
                    <th className="px-[20px] py-[13px]">User Name</th>
                    <th className="px-[20px] py-[13px]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {[...Array(4)].map((_, index) => (
                    <tr key={index} className="grid border-b">
                      <td className="flex items-center gap-[8px] px-[20px] py-[13px]">
                        <Skeleton
                          circle
                          height={20}
                          width={20}
                          className="flex-shrink-0"
                        />
                        <Skeleton width={120} height={16} />
                      </td>
                      <td className="px-[20px] py-[13px]">
                        <Skeleton width={100} height={16} />
                      </td>
                      <td className="flex items-center justify-between gap-2 px-[20px] py-[13px]">
                        <Skeleton
                          width={90}
                          height={30}
                          className="rounded-[10px]"
                        />
                        <Skeleton width={24} height={24} className="rounded" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            accounts &&
            accounts?.length > 0 && (
              <div className="fb-list whatsapp_table overflow-x-auto">
                <table className="instagram_table mt-[10px] min-w-full overflow-hidden rounded-[10px] border border-[rgba(167,80,80,0.1)]">
                  <thead className="flex w-full rounded-t-[10px] border border-[rgba(0,0,0,0.10)] bg-gray-100 text-gray-700">
                    <tr className="grid">
                      <th className="px-[20px] py-[13px]">
                        Account name | User name
                      </th>
                      <th className="px-[20px] py-[13px]">Details</th>
                      <th className="px-[20px] py-[13px]">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts?.map((account: any, index: number) => (
                      <Fragment key={index}>
                        <tr
                          key={`${account.id}-${index}`}
                          className={`grid ${
                            data?.showPrompt ? "block" : "table-row"
                          } border-b`}
                        >
                          <td className="flex items-center gap-[8px] px-[20px] py-[13px]">
                            <img
                              src="https://img.freepik.com/free-vector/blue-circle-with-white-user_78370-4707.jpg"
                              alt={account.accountName}
                              className="h-[30px] w-[30px] rounded-[50%]"
                            />
                            <p className="flex max-w-[107px] flex-col">
                              {" "}
                              <span className="block truncate">
                                {" "}
                                {account.accountName}
                              </span>
                              <span className="block truncate">
                                {" "}
                                @{account.username}
                              </span>
                            </p>

                            {account?.connected && (
                              <span className="rounded-[5px] bg-[#3395dd] px-[6px] py-[4px] text-[12px] leading-[100%] text-white">
                                Connected
                              </span>
                            )}
                          </td>
                          <td className="flex flex-col items-start gap-[10px] px-[20px] py-[13px]">
                            <p>
                              {" "}
                              <span className="font-medium">
                                {account.account_type}
                              </span>{" "}
                              Account
                            </p>
                            <p>
                              {" "}
                              <span className="font-medium">
                                {account.followers_count}
                              </span>{" "}
                              Followers
                            </p>
                            <p>
                              {" "}
                              <span className="font-medium">
                                {" "}
                                {account.follows_count}
                              </span>{" "}
                              Following
                            </p>
                            {/* {account.account_type}
                            {account.followers_count}
                            {account.follows_count} */}
                            {/* {account.biography} */}
                          </td>
                          <td className="justify-between px-[20px] py-[13px]">
                            {account?.connected ? (
                              <button
                                disabled={!!connectionStatus[account._id]}
                                className="social_disconnect flex h-[30px] items-center gap-[8px] rounded-[10px] border border-solid border-[rgba(251,95,102,0.1)] bg-[#FEE6E7] px-[15px] py-[10px] text-[14px] leading-[110%] text-primary-clr"
                                onClick={() => {
                                  handleAcc(
                                    !account?.connected
                                      ? "connect"
                                      : "disconnect",
                                    account,
                                  );
                                }}
                              >
                                <DisconnectIcon />{" "}
                                {connectionStatus[account._id]
                                  ? "Disconnecting..."
                                  : "Disconnect"}
                              </button>
                            ) : (
                              <button
                                disabled={!!connectionStatus[account._id]}
                                className="border-[rgba(0, 0, 0, 0.10)] social_connect flex h-[30px] items-center gap-[8px] rounded-[10px] border border-solid bg-[#F9F9F9] px-[15px] py-[10px] text-[14px] leading-[110%] text-[#666]"
                                onClick={() => {
                                  handleAcc(
                                    !account?.connected
                                      ? "connect"
                                      : "disconnect",
                                    account,
                                  );
                                }}
                              >
                                <ConnectIcon />{" "}
                                {connectionStatus[account._id]
                                  ? "Connecting..."
                                  : "Connect"}
                              </button>
                            )}
                            <span
                              onClick={() => {
                                setIsInstagramDelete(true);
                                // deleteConfig(account?._id);
                              }}
                              className="flex-cnt h-[30px] w-[30px] cursor-pointer rounded-lg"
                            >
                              <DeleteBot />
                            </span>
                          </td>
                        </tr>

                        {account?.showPrompt && (
                          <ModalWrapper>
                            {/* <AnimatePresence> */}
                            <div className="createbot-modal">
                              <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                              >
                                <div className="flex-cnt mx-4 w-[680px] flex-col rounded-[10px] bg-[linear-gradient(180deg,_#FB5F66_-51.8%,_#FFFFFF_70.59%)] p-[40px]">
                                  <ErrSvgIcon />
                                  <h2 className="mb-[10px] mt-[20px] text-[18px] font-medium text-text-clr">
                                    Replace Bot
                                  </h2>
                                  <p className="mb-[30px] text-center text-[14px] text-[#444444CC]">
                                    This account is already connected to another
                                    bot. <br />
                                    Do you want to replace the earlier bot with
                                    current bot?
                                  </p>

                                  <div className="flex justify-end gap-4">
                                    <motion.button
                                      whileHover={{
                                        scale: 1.1,
                                        transition: { duration: 0.5 },
                                      }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => {
                                        handlePrompt(false, account?._id);
                                      }}
                                      disabled={isLoading}
                                      className="h-[45px] w-[170px] rounded-[6px] border border-primary-clr text-[14px] text-primary-clr"
                                    >
                                      Cancel
                                    </motion.button>
                                    <motion.button
                                      whileHover={{
                                        scale: 1.1,
                                        transition: { duration: 0.5 },
                                      }}
                                      whileTap={{ scale: 0.9 }}
                                      onClick={() => {
                                        handleAcc("connect", account);
                                      }}
                                      disabled={isLoading || connection}
                                      className="w-[170px] rounded-[6px] border border-primary-clr bg-primary-clr text-[14px] text-white"
                                    >
                                      Replace Bot
                                    </motion.button>
                                  </div>
                                </div>
                              </motion.div>
                            </div>
                            {/* </AnimatePresence> */}
                          </ModalWrapper>
                        )}
                        {isInstagramDelete ? (
                          <ModalWrapper>
                            <BooleanModal
                              onConfirm={() => deleteConfig(account?._id)}
                              onCancel={() => setIsInstagramDelete(false)}
                              content={{
                                title: "Logout of Instagram",
                                message: `Logging out will stop all active workflows running on this Instagram account. \n Are you sure you want to continue?`,
                              }}
                              buttons={{
                                confirm: "Logout",
                                cancel: "Cancel",
                              }}
                            />
                          </ModalWrapper>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {!accounts?.length && (search ? <NoResultFound /> : <h2></h2>)}

          <span
            style={{ color: "#FB5F66" }}
            onClick={() => addNewConfig()}
            className="mt-15 flex cursor-pointer items-center gap-[8px] !text-[13px] hover:underline"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={13}
              height={13}
              viewBox="0 0 13 13"
              fill="none"
            >
              <path
                d="M5.64045 12.5C5.49064 12.5 5.3633 12.4551 5.25843 12.3652C5.16854 12.2603 5.1236 12.133 5.1236 11.9831V7.30899H0.516854C0.367041 7.30899 0.2397 7.26405 0.134831 7.17416C0.0449438 7.06929 0 6.94195 0 6.79214V6.07303C0 5.92322 0.0449438 5.80337 0.134831 5.71348C0.2397 5.60861 0.367041 5.55618 0.516854 5.55618H5.1236V1.01685C5.1236 0.867041 5.16854 0.747191 5.25843 0.657304C5.3633 0.552435 5.49064 0.5 5.64045 0.5H6.42697C6.57678 0.5 6.69663 0.552435 6.78652 0.657304C6.89139 0.747191 6.94382 0.867041 6.94382 1.01685V5.55618H11.573C11.7228 5.55618 11.8427 5.60861 11.9326 5.71348C12.0375 5.80337 12.0899 5.92322 12.0899 6.07303V6.79214C12.0899 6.94195 12.0375 7.06929 11.9326 7.17416C11.8427 7.26405 11.7228 7.30899 11.573 7.30899H6.94382V11.9831C6.94382 12.133 6.89139 12.2603 6.78652 12.3652C6.69663 12.4551 6.57678 12.5 6.42697 12.5H5.64045Z"
                fill="#FB5F66"
              />
            </svg>
            Add new configuration
          </span>
        </div>
      </div>
    </>
  );
}

export const DeleteIconBlack = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M5.05334 2.65618L5.17934 1.90593C5.27098 1.36185 5.33971 0.955231 6.30762 0.955231H7.80816C8.77607 0.955231 8.85053 1.38476 8.93644 1.91166L9.06244 2.65618"
      stroke="#666666"
      strokeWidth="0.859077"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.218 3.65711C10.3108 3.46812 8.39221 3.37076 6.4793 3.37076C5.3453 3.37076 4.2113 3.42803 3.0773 3.54257L1.90894 3.65711"
      stroke="#666666"
      strokeWidth="0.859077"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.9869 5.08875L10.6146 10.8559C10.5516 11.7551 10.5 12.4538 8.90214 12.4538H5.22523C3.62732 12.4538 3.57578 11.7551 3.51278 10.8559L3.1405 5.08875"
      stroke="#666666"
      strokeWidth="0.859077"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.10535 9.97205H8.01253"
      stroke="#666666"
      strokeWidth="0.859077"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.63367 7.33093H8.4973"
      stroke="#666666"
      strokeWidth="0.859077"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const ErrSvgIcon = () => (
  <svg
    width={80}
    height={80}
    viewBox="0 0 80 80"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx={40} cy={40} r={40} fill="white" fillOpacity={0.2} />
    <circle
      cx={39.9998}
      cy={40.0018}
      r={35.0877}
      fill="white"
      fillOpacity={0.36}
    />
    <rect
      x={11.2275}
      y={11.2305}
      width={57.5439}
      height={57.5439}
      rx={28.7719}
      fill="white"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M53.2913 39.9987C53.2913 47.3393 47.3403 53.2904 39.9997 53.2904C32.659 53.2904 26.708 47.3393 26.708 39.9987C26.708 32.6581 32.659 26.707 39.9997 26.707C47.3403 26.707 53.2913 32.6581 53.2913 39.9987ZM39.9997 32.7487C40.3201 32.7487 40.6275 32.876 40.8541 33.1026C41.0807 33.3292 41.208 33.6366 41.208 33.957V41.207C41.208 41.5275 41.0807 41.8348 40.8541 42.0615C40.6275 42.2881 40.3201 42.4154 39.9997 42.4154C39.6792 42.4154 39.3719 42.2881 39.1453 42.0615C38.9186 41.8348 38.7913 41.5275 38.7913 41.207V33.957C38.7913 33.6366 38.9186 33.3292 39.1453 33.1026C39.3719 32.876 39.6792 32.7487 39.9997 32.7487ZM39.9997 47.2487C40.3201 47.2487 40.6275 47.1214 40.8541 46.8948C41.0807 46.6682 41.208 46.3608 41.208 46.0404C41.208 45.7199 41.0807 45.4125 40.8541 45.1859C40.6275 44.9593 40.3201 44.832 39.9997 44.832C39.6792 44.832 39.3719 44.9593 39.1453 45.1859C38.9186 45.4125 38.7913 45.7199 38.7913 46.0404C38.7913 46.3608 38.9186 46.6682 39.1453 46.8948C39.3719 47.1214 39.6792 47.2487 39.9997 47.2487Z"
      fill="#FB5F66"
    />
  </svg>
);
